import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { statsApi } from 'api/endpoints/stats.api';
import {
  DashboardStats,
  AppStats,
  ConversionStats,
  UserAnalytics,
  PushPerformanceResponse,
  PushStatsResponse,
} from 'types/push.types';

export type StatsRequest =
  | 'dashboard'
  | 'appStats'
  | 'pushStats'
  | 'campaignStats'
  | 'conversions'
  | 'userAnalytics'
  | 'pushPerformance'
  | 'campaignComparison'
  | 'segmentComparison'
  | 'export';

interface StatsState {
  dashboard: DashboardStats | null;
  appStats: AppStats | null;
  pushStats: PushStatsResponse | null;
  campaignStats: any | null;
  conversions: { app_id: number; period: string; conversions: ConversionStats } | null;
  userAnalytics: {
    app_id: number;
    period: string;
    group_by: string;
    analytics: UserAnalytics[] | null;
  } | null;
  pushPerformance: PushPerformanceResponse | null;
  campaignComparison: {
    campaigns: Array<{ id: number; name: string; stats: any }>;
    warnings?: { inaccessible_campaigns: number[]; message: string };
  } | null;
  segmentComparison: {
    app_id: number;
    period: string;
    segments: any[];
    warnings?: { inaccessible_segments: number[]; message: string };
  } | null;

  loading: Record<StatsRequest, boolean>;
  activeRequestId: Partial<Record<StatsRequest, string>>;
  error: string | null;
}

export type StatsRootState = { stats: StatsState };

const emptyLoading: Record<StatsRequest, boolean> = {
  dashboard: false,
  appStats: false,
  pushStats: false,
  campaignStats: false,
  conversions: false,
  userAnalytics: false,
  pushPerformance: false,
  campaignComparison: false,
  segmentComparison: false,
  export: false,
};

const initialState: StatsState = {
  dashboard: null,
  appStats: null,
  pushStats: null,
  campaignStats: null,
  conversions: null,
  userAnalytics: null,
  pushPerformance: null,
  campaignComparison: null,
  segmentComparison: null,
  loading: { ...emptyLoading },
  activeRequestId: {},
  error: null,
};

export const fetchDashboard = createAsyncThunk(
  'stats/fetchDashboard',
  async (appId: number) => statsApi.getDashboard(appId),
);

export const fetchAppStats = createAsyncThunk(
  'stats/fetchAppStats',
  async ({ appId, period }: { appId: number; period?: string }) => statsApi.getAppStats(appId, period),
);

export const fetchPushStats = createAsyncThunk(
  'stats/fetchPushStats',
  async (messageId: string) => statsApi.getPushStats(messageId),
);

export const fetchCampaignStats = createAsyncThunk(
  'stats/fetchCampaignStats',
  async (campaignId: number) => statsApi.getCampaignStats(campaignId),
);

export const fetchConversions = createAsyncThunk(
  'stats/fetchConversions',
  async ({ appId, period = '30d' }: { appId: number; period?: string }) => {
    const result = await statsApi.getConversions(appId, period);
    if (result && typeof result === 'object' && 'conversions' in (result as any)) {
      return result as unknown as { app_id: number; period: string; conversions: ConversionStats };
    }
    return { app_id: appId, period, conversions: result };
  },
);

export const fetchUserAnalytics = createAsyncThunk(
  'stats/fetchUserAnalytics',
  async ({ appId, period = '30d', groupBy = 'day' }: { appId: number; period?: string; groupBy?: string }) =>
    statsApi.getUserAnalytics(appId, period, groupBy),
);

export const fetchPushPerformance = createAsyncThunk(
  'stats/fetchPushPerformance',
  async ({ appId, period }: { appId: number; period?: string }) => statsApi.getPushPerformance(appId, period),
);

export const compareCampaigns = createAsyncThunk(
  'stats/compareCampaigns',
  async (campaignIds: number[]) => statsApi.compareCampaigns(campaignIds),
);

export const compareSegments = createAsyncThunk(
  'stats/compareSegments',
  async (data: { app_id: number; segment_ids: number[]; period?: string }) => statsApi.compareSegments(data),
);

export const exportStats = createAsyncThunk(
  'stats/export',
  async (params: { app_id: number; type: 'push' | 'users' | 'conversions'; period?: '7d' | '30d' | '90d' }) => {
    await statsApi.downloadStatsCSV(params);
  },
);

const REQUEST_BY_TYPE: Record<string, StatsRequest> = {
  [fetchDashboard.typePrefix]: 'dashboard',
  [fetchAppStats.typePrefix]: 'appStats',
  [fetchPushStats.typePrefix]: 'pushStats',
  [fetchCampaignStats.typePrefix]: 'campaignStats',
  [fetchConversions.typePrefix]: 'conversions',
  [fetchUserAnalytics.typePrefix]: 'userAnalytics',
  [fetchPushPerformance.typePrefix]: 'pushPerformance',
  [compareCampaigns.typePrefix]: 'campaignComparison',
  [compareSegments.typePrefix]: 'segmentComparison',
  [exportStats.typePrefix]: 'export',
};

const ERROR_MESSAGES: Record<StatsRequest, string> = {
  dashboard: 'Не удалось загрузить сводку по приложению',
  appStats: 'Не удалось загрузить статистику приложения',
  pushStats: 'Не удалось загрузить статистику push-уведомления',
  campaignStats: 'Не удалось загрузить статистику кампании',
  conversions: 'Не удалось загрузить конверсии',
  userAnalytics: 'Не удалось загрузить аналитику пользователей',
  pushPerformance: 'Не удалось загрузить эффективность push-уведомлений',
  campaignComparison: 'Не удалось сравнить кампании',
  segmentComparison: 'Не удалось сравнить сегменты',
  export: 'Не удалось выгрузить статистику',
};

const requestOf = (actionType: string): StatsRequest | undefined =>
  REQUEST_BY_TYPE[actionType.slice(0, actionType.lastIndexOf('/'))];

type StatsLifecycle = 'pending' | 'fulfilled' | 'rejected';

interface StatsAction {
  type: string;
  meta: { requestId: string; condition?: boolean };
}

const isStatsAction =
  (lifecycle: StatsLifecycle) =>
  (action: StatsAction): action is StatsAction =>
    action.type.endsWith(`/${lifecycle}`) && requestOf(action.type) !== undefined;

const isCurrent = (
  active: Partial<Record<StatsRequest, string>>,
  request: StatsRequest,
  requestId: string,
) => active[request] === requestId;

const statsSlice = createSlice({
  name: 'stats',
  initialState,
  reducers: {
    clearStats: (state) => {
      state.dashboard = null;
      state.appStats = null;
      state.pushStats = null;
      state.campaignStats = null;
      state.conversions = null;
      state.userAnalytics = null;
      state.pushPerformance = null;
      state.campaignComparison = null;
      state.segmentComparison = null;
      state.loading = { ...emptyLoading };
      state.activeRequestId = {};
      state.error = null;
    },
    clearError: (state) => {
      state.error = null;
    },
    clearComparisons: (state) => {
      state.campaignComparison = null;
      state.segmentComparison = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDashboard.fulfilled, (state, action) => {
        if (!isCurrent(state.activeRequestId, 'dashboard', action.meta.requestId)) return;
        state.dashboard = action.payload;
      })
      .addCase(fetchAppStats.fulfilled, (state, action) => {
        if (!isCurrent(state.activeRequestId, 'appStats', action.meta.requestId)) return;
        state.appStats = action.payload;
      })
      .addCase(fetchPushStats.fulfilled, (state, action) => {
        if (!isCurrent(state.activeRequestId, 'pushStats', action.meta.requestId)) return;
        state.pushStats = action.payload;
      })
      .addCase(fetchCampaignStats.fulfilled, (state, action) => {
        if (!isCurrent(state.activeRequestId, 'campaignStats', action.meta.requestId)) return;
        state.campaignStats = action.payload;
      })
      .addCase(fetchConversions.fulfilled, (state, action) => {
        if (!isCurrent(state.activeRequestId, 'conversions', action.meta.requestId)) return;
        state.conversions = action.payload;
      })
      .addCase(fetchUserAnalytics.fulfilled, (state, action) => {
        if (!isCurrent(state.activeRequestId, 'userAnalytics', action.meta.requestId)) return;
        state.userAnalytics = action.payload;
      })
      .addCase(fetchPushPerformance.fulfilled, (state, action) => {
        if (!isCurrent(state.activeRequestId, 'pushPerformance', action.meta.requestId)) return;
        state.pushPerformance = action.payload;
      })
      .addCase(compareCampaigns.fulfilled, (state, action) => {
        if (!isCurrent(state.activeRequestId, 'campaignComparison', action.meta.requestId)) return;
        state.campaignComparison = action.payload;
      })
      .addCase(compareSegments.fulfilled, (state, action) => {
        if (!isCurrent(state.activeRequestId, 'segmentComparison', action.meta.requestId)) return;
        state.segmentComparison = action.payload;
      })
      .addMatcher(isStatsAction('pending'), (state, action) => {
        const request = requestOf(action.type);
        if (!request) return;
        state.activeRequestId[request] = action.meta.requestId;
        state.loading[request] = true;
        state.error = null;
      })
      .addMatcher(isStatsAction('fulfilled'), (state, action) => {
        const request = requestOf(action.type);
        if (!request || !isCurrent(state.activeRequestId, request, action.meta.requestId)) return;
        state.loading[request] = false;
      })
      .addMatcher(isStatsAction('rejected'), (state, action) => {
        const request = requestOf(action.type);
        if (!request || action.meta.condition) return;
        if (!isCurrent(state.activeRequestId, request, action.meta.requestId)) return;
        state.loading[request] = false;
        state.error = ERROR_MESSAGES[request];
      });
  },
});

export const { clearStats, clearError, clearComparisons } = statsSlice.actions;

export const selectDashboard = (state: StatsRootState) => state.stats.dashboard;
export const selectAppStats = (state: StatsRootState) => state.stats.appStats;
export const selectPushStats = (state: StatsRootState) => state.stats.pushStats;
export const selectCampaignStats = (state: StatsRootState) => state.stats.campaignStats;
export const selectConversions = (state: StatsRootState) => state.stats.conversions;
export const selectUserAnalytics = (state: StatsRootState) => state.stats.userAnalytics;
export const selectPushPerformance = (state: StatsRootState) => state.stats.pushPerformance;
export const selectCampaignComparison = (state: StatsRootState) => state.stats.campaignComparison;
export const selectSegmentComparison = (state: StatsRootState) => state.stats.segmentComparison;
export const selectStatsError = (state: StatsRootState) => state.stats.error;

export const selectStatsLoading = (state: StatsRootState) =>
  Object.values(state.stats.loading).some(Boolean);

export const selectLoadingFor = (request: StatsRequest) => (state: StatsRootState) =>
  state.stats.loading[request];

export const selectLoadingAny = (requests: StatsRequest[]) => (state: StatsRootState) =>
  requests.some((request) => state.stats.loading[request]);

export default statsSlice.reducer;